import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {saveAs} from 'file-saver';
import * as luxon from 'luxon';
import {ToastrService} from 'ngx-toastr';
import {interval} from 'rxjs';
import {debounce, finalize} from 'rxjs/operators';
import Swal from 'sweetalert2';
import {AppSelectOption} from '../../../../../../../_base-shared/contracts/common.interface';
import {LaravelResourceResponse} from '../../../../../../../_base-shared/contracts/laravel-response.interface';
import {Case} from '../../../../../../../_base-shared/models/Case/Case';
import {Creditor} from '../../../../../../../_base-shared/models/Entity/Creditor';
import {CasePaymentPlan} from '../../../../../../../_base-shared/models/Payment/CasePaymentPlan';
import {FinancialOverview} from '../../../../../../../_base-shared/models/Payment/FinancialOverview';
import {PaymentMethod, PaymentMethodType} from '../../../../../../../_base-shared/models/Payment/PaymentMethod';
import {PaymentProcessorType} from '../../../../../../../_base-shared/models/Payment/PaymentProcessor';
import {Product} from '../../../../../../../_base-shared/models/Product';
import {User} from '../../../../../../../_base-shared/models/User/User';
import {CardInfoModalComponent} from '../../../../_shared/components/card-info-modal/card-info-modal.component';
import {
  RequestPaymentModalComponent
} from '../../../../_shared/components/request-payment-modal/request-payment-modal.component';
import {MainGlobalEventService} from '../../../../_shared/services/main-global-event.service';
import {EmailPreviewComponent} from '../../../app-document/email-preview/email-preview.component';
import {FinancialOverviewService} from '../../../payment/financial-overview.service';
import {PaymentMethodService} from '../../../payment/payment-method.service';
import {CaseCreditorService} from '../../case-creditor.service';
import {CasePaymentPlanService} from '../../case-payment-plan.service';
import {CaseService} from '../../case.service';
import {ProductService} from '../../product.service';
import { DateTime } from 'luxon';

@Component({
  selector:    'app-case-payment-editor',
  templateUrl: './case-payment-editor.component.html',
  styleUrls:   ['./case-payment-editor.component.scss']
})
export class CasePaymentEditorComponent implements OnInit {
  public authUser: User;
  public case: Case;
  public isLoading                            = 0;
  public submittingPaymentPlan: boolean;
  public paymentPlanForm: UntypedFormGroup;
  public paymentPlanResponse: LaravelResourceResponse;
  public submittingPaymentConfiguration: boolean;
  public paymentConfigurationForm: UntypedFormGroup;
  public paymentConfigResponse: LaravelResourceResponse;
  public financialOverview: FinancialOverview;
  public phaseOnePaid: boolean; // TODO:
  public phaseTwoPaid: boolean; // TODO:
  public taxRate                              = 1.21;
  public paymentMethods: Array<PaymentMethod> = [];
  public daysInMonth: Array<AppSelectOption>  = [];
  public summaryResponse: LaravelResourceResponse;

  public sendLinksError             = '';
  public creditors: Array<Creditor> = [];
  public products: Array<Product>   = [];

  // Payment Plan
  public selectedProduct: Product;
  public casePaymentPlanOutdated: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private changeRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private caseService: CaseService,
    private caseCreditorService: CaseCreditorService,
    private globalEventsService: MainGlobalEventService,
    private paymentMethodService: PaymentMethodService,
    private productService: ProductService,
    private casePaymentPlanService: CasePaymentPlanService,
    private financialOverviewService: FinancialOverviewService
  ) {
  }

  ngOnInit(): void {
    this.globalEventsService.authUser$.subscribe(user => this.authUser = user);
    this.route.parent.paramMap.subscribe(params => {
      const caseId = +params.get('id');
      this.isLoading++;
      this.caseService.get(caseId,
        ['product.payment_plans.payment_plan_phases', 'debt_payment_plan', 'creditors', 'distribution']
      )
        .pipe(finalize(() => this.isLoading--))
        .subscribe(result => {
          this.case = result.data;
          this.fetchProducts();
          this.fetchPaymentMethods();
          this.displayDistributionWarningModal();
        });
    });

    this.financialOverviewService.financialOverview$.subscribe(overview => this.financialOverview = overview);
    this.daysInMonth = [];
    for (let i = 1; i <= 28; i++) {
      this.daysInMonth.push({label: i.toString(10), value: i});
    }
  }

  public isComponentDirty(): boolean {
    return this.paymentPlanForm.dirty || this.paymentConfigurationForm.dirty;
  }

  public isComponentValid(): boolean {
    return this.paymentPlanForm.valid || this.paymentConfigurationForm.valid;
  }

  public submitComponent() {
    if (this.paymentPlanForm.dirty) {
      this.submitPaymentPlanForm(this.paymentPlanForm);
    }
    if (this.paymentConfigurationForm.dirty) {
      this.submitPaymentConfigurationForm(this.paymentConfigurationForm);
    }

    return true;
  }

  public hasError(form: UntypedFormGroup, formControlName: string) {
    return form.get(formControlName)?.touched && this.paymentPlanForm.get(formControlName)?.invalid;
  }

  public sendSepaLink(send_to) {
    this.sendLinksError = '';
    const amount        = this.case.debt_payment_plan.initial_fee;

    if (!this.case.id || !amount) {
      this.sendLinksError = this.translate.instant('CASES.single.save-case-error');

      return this.toastr.error(this.translate.instant('CASES.single.save-case-error'));
    }

    const data = {
      caseId: this.case.id,
      send_to
    };
    this.caseService.sendSepaLink(data).subscribe(
      () => {
        this.toastr.success(this.translate.instant('CASES.single.send-link-success'));
      },
      err => {
        console.log(err);
        this.toastr.error(this.translate.instant('CASES.single.send-link-error'));
      }
    );
  }

  public requestPaymentDialog(paymentMethodType: PaymentMethodType, paymentProcessorType: PaymentProcessorType) {
    this.sendLinksError = '';
    const amount        = this.case.debt_payment_plan.initial_fee;

    if (!this.case.id || !amount) {
      this.sendLinksError = this.translate.instant('CASES.single.save-case-error');

      return this.toastr.error(this.translate.instant('CASES.single.save-case-error'));
    }

    const dialogRef = this.dialog.open(RequestPaymentModalComponent, {
      width:        '50%',
      minHeight:    200,
      disableClose: true,
      autoFocus:    false,
      data:         {
        caseId:         this.case.id,
        paymentMethodType,
        paymentProcessorType,
        amount,
        disabledAmount: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.success) {
          this.toastr.success(this.translate.instant('CASES.single.request-payment-success'),
            this.translate.instant('SHARED.success'));
        } else {
          this.toastr.error(this.translate.instant('CASES.single.request-payment-error'),
            this.translate.instant('SHARED.error'));
        }
      }
    });
  }

  public sendContract($event, sendTo) {
    $event.preventDefault();
    if (!this.paymentConfigurationForm.get('initial_fee').value){
      this.toastr.error(this.translate.instant('CASES.single.initial-fee-required'));
      return;
    }
    const data = {
      caseId:  this.case.id,
      send_to: sendTo
    };
    this.caseService.sendContract(data).subscribe(next => {
        this.toastr.success(this.translate.instant('CASES.single.send-contract-success'));
      }, error => {
        this.toastr.error(this.translate.instant('CASES.single.send-contract-error'));
        this.summaryResponse = error.error;
      }
    );
  }

  public previewEmail($event, emailType: 'SendInvoiceToCreditorNotification' | 'NewContract', caseId: number,
                      creditorId: number) {
    $event.preventDefault();

    this.caseCreditorService.previewEmail(caseId, creditorId, emailType).subscribe(result => {
        const emailHeader = {
          from:    result.data.from,
          to:      result.data.to,
          cc:      result.data.cc,
          subject: result.data.subject
        };
        const dialogRef   = this.dialog.open(EmailPreviewComponent, {
          width:  '310mm',
          height: '100vh',
          data:   {
            title:       'Creditor Invoice Preview',
            previewType: 'email',
            emailHeader,
            emailBody:   result.data.body,
            attachments: result.data.attachments
          }
        });

        dialogRef.afterClosed().subscribe(res => {
          if (res?.action === 'send-email') {
            this.caseCreditorService.sendEmail(caseId, creditorId, emailType).subscribe(
              r => this.toastr.success(
                this.translate.instant('CASES.single.reclama.toast-messages.invoice-sent-success')
              ),
              errorResponse => {
                this.parseValidationErrors(errorResponse);
                this.translate.instant('CASES.single.reclama.toast-messages.invoice-sent-error');
              }
            );
          }
          if (res?.action === 'save-and-download-document' && res?.params?.type) {
            this.caseCreditorService.saveAndDownloadDocument(caseId, creditorId, res.params.type).subscribe(
              r => {
                if (r.type === 4) {
                  const fileName = res.params?.type + '_' + DateTime.local().toFormat('yyyy-LL-dd_HH-mm') + '.pdf';
                  saveAs(r.body, fileName);
                  this.toastr.success('Document downloaded');
                }
              },
              errorResponse => this.parseValidationErrors(errorResponse)
            );
          }
        });
      }, errorResponse => this.parseValidationErrors(errorResponse)
    );
  }

  public openCardForm(type) {
    const amount = this.case.debt_payment_plan.initial_fee;

    if (this.isComponentDirty()) {
      this.submitComponent();
    }
    const dialogRef = this.dialog.open(CardInfoModalComponent, {
      width:        '60%',
      minHeight:    200,
      disableClose: true,
      data:         {
        paymentType: type,
        amount:      type === 'verify' ? 0.01 : amount,
        caseId:      this.case.id
      }
    });
  }

  private fetchPaymentMethods(): void {
    this.isLoading++;
    this.paymentMethodService.index().pipe(finalize(() => this.isLoading--)).subscribe(
      result => this.paymentMethods = result.data,
      error => console.log(error)
    );
  }

  private fetchProducts(): void {
    this.isLoading++;
    this.productService.index({select_all: 1}).pipe(finalize(() => this.isLoading--)).subscribe(
      result => {
        this.products        = result.data;
        this.selectedProduct = this.products.find(product => product.id === this.case.product_id);
        if (!this.case.debt_payment_plan) {
          this.case.debt_payment_plan = this.getDefaultPaymentPlan(this.case);
        }
        this.buildPaymentPlanForm(this.case.debt_payment_plan);
        this.buildPaymentConfigurationForm(this.case.debt_payment_plan);
      }
    );
  }

  private getDefaultPaymentPlan(clientCase: Case): CasePaymentPlan {
    const selectedPaymentPlan = clientCase.product.payment_plans.find(
      plan => plan.is_joint === clientCase.joint_application
    );

    const paymentPlan = new CasePaymentPlan();

    return paymentPlan;
  }

  public getPaymentMethodSlug(paymentMethodId: number): string {
    const paymentMethod = this.paymentMethods?.find(pMethod => {
      return Number(pMethod.id) === Number(paymentMethodId);
    });

    return paymentMethod?.slug;
  }

  public contractButtonDisabled(): boolean {
    return !this.validIdCards(this.case) || !this.case.product_assigned_at;
  }

  private parseValidationErrors(errorResponse: any) {
    const error = errorResponse.error;
    if (+error.code === 422 && error.errors) {
      for (const [key, value] of Object.entries(error.errors)) {
        const valueArray = value as any;
        valueArray.forEach(e => this.toastr.error(e));
      }
    }
  }

  public validIdCards(clientCase: Case): boolean {
    return clientCase.joint_application ?
      !!(clientCase.client?.id_card && clientCase.partner?.id_card) :
      !!(clientCase.client?.id_card);
  }

  private buildPaymentPlanForm(casePaymentPlan: CasePaymentPlan): void {
    this.paymentPlanForm = this.fb.group({
      initial_fee:       [casePaymentPlan.initial_fee],
      percentage_agreed: [casePaymentPlan.percentage_agreed, [Validators.min(0), Validators.max(100)]]
    });
  }

  private buildPaymentConfigurationForm(paymentConfig: CasePaymentPlan) {
    this.paymentConfigurationForm = this.fb.group({
      initial_fee:       [
        paymentConfig.initial_fee === 0 ? null : paymentConfig.initial_fee,
        [Validators.min(1), Validators.max(5000)]],
      payment_method_id: [paymentConfig.payment_method_id],
      iban:              [paymentConfig.iban],
      charge_amount:     [paymentConfig.charge_amount, [Validators.min(1), Validators.max(5000)]]
    });
  }

  public submitPaymentPlanForm(paymentPlanForm: UntypedFormGroup) {
    if (paymentPlanForm.invalid) {
      paymentPlanForm.markAllAsTouched();
      return;
    }
    this.submittingPaymentPlan = true;
    this.casePaymentPlanService.upsertPaymentPlanFromForm(this.case.id, paymentPlanForm.getRawValue())
      .pipe(finalize(() => this.submittingPaymentPlan = false))
      .subscribe(
        () => {
          this.paymentPlanForm.markAsPristine();
          this.toastr.success(this.translate.instant('CASES.editor.payment.payment_plan.result.success'));
        },
        error => {
          this.paymentPlanResponse = error.error;
          this.toastr.error(this.translate.instant('CASES.editor.payment.payment_plan.result.error'),
            error.error.message);
        }
      );
  }

  public submitPaymentConfigurationForm(paymentConfigurationForm: UntypedFormGroup) {
    if (paymentConfigurationForm.invalid) {
      paymentConfigurationForm.markAllAsTouched();
      return;
    }
    this.submittingPaymentConfiguration = true;
    this.casePaymentPlanService.upsertPaymentConfig(this.case.id, paymentConfigurationForm.getRawValue())
      .pipe(finalize(() => this.submittingPaymentConfiguration = false))
      .subscribe(
        () => {
          this.paymentConfigurationForm.markAsPristine();
          this.toastr.success(this.translate.instant('CASES.editor.payment.payment_config.result.success'));
        },
        error => {
          this.paymentConfigResponse = error.error;
          this.toastr.error(
            this.translate.instant('CASES.editor.payment.payment_config.result.error'), error.error.message
          );
        }
      );
  }

  public updateUserIdCard(idCard: string, relation: 'client' | 'partner') {
    this.case[relation].id_card = idCard;
  }

  public updatedClient(newUser: User, relation: 'client' | 'partner') {
    this.case[relation] = newUser;
  }

  public displayDistributionWarningModal(): void {
    if (this.case.distribution && this.case.distribution.distribution_batch_id) {
      Swal.fire({
        text:               this.translate.instant('DISTRIBUTION.modals.prior-authorization'),
        icon:               'warning',
        showCancelButton:   false,
        confirmButtonText:  'OK',
        confirmButtonColor: '#4267B2'
      });
    } else if (this.case.distribution) {
      Swal.fire({
        text:               this.translate.instant('DISTRIBUTION.modals.notify-of-changes'),
        icon:               'warning',
        showCancelButton:   false,
        confirmButtonText:  'OK',
        confirmButtonColor: '#4267B2'
      });
    }
  }

}
